import { AlertTriangle, Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "shared/ui/alert"
import { Button } from "shared/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "shared/ui/dialog"
import { RadioGroup, RadioGroupItem } from "shared/ui/radio-group"
import { Textarea } from "shared/ui/textarea"
import type { CancelReasonOption } from "modules/permit/api/permitService"

interface CancelPermitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  permitNumber: string
  cancelReasons: CancelReasonOption[]
  selectedReason: string
  onSelectedReasonChange: (value: string) => void
  reasonDetail: string
  onReasonDetailChange: (value: string) => void
  isLoadingReasons: boolean
  isCancelling: boolean
  error: string | null
  onConfirm: () => void
  onResetError: () => void
}

export function CancelPermitDialog({
  open,
  onOpenChange,
  permitNumber,
  cancelReasons,
  selectedReason,
  onSelectedReasonChange,
  reasonDetail,
  onReasonDetailChange,
  isLoadingReasons,
  isCancelling,
  error,
  onConfirm,
  onResetError,
}: CancelPermitDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen)
        if (!nextOpen) {
          onResetError()
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            ยกเลิกเอกสาร Permit
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-gray-700">
            เลขที่เอกสาร: <span className="font-semibold text-gray-900">{permitNumber || "-"}</span>
          </div>
          <Alert className="border-amber-200 bg-amber-50 text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              การยกเลิกเอกสารนี้ไม่สามารถย้อนกลับได้ และจะถูกบันทึกเป็นประวัติ
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900">เหตุผลในการยกเลิก *</p>
            {isLoadingReasons ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                กำลังโหลดเหตุผลการยกเลิก...
              </div>
            ) : cancelReasons.length === 0 ? (
              <div className="text-sm text-red-600">
                ไม่พบรายการเหตุผลการยกเลิก โปรดลองใหม่หรือแจ้งผู้ดูแลระบบ
              </div>
            ) : (
              <RadioGroup
                value={selectedReason}
                onValueChange={(value) => {
                  onSelectedReasonChange(value)
                  onResetError()
                }}
                className="space-y-2"
              >
                {cancelReasons.map((reason) => (
                  <label
                    key={reason.code}
                    className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2 text-sm hover:border-gray-300"
                  >
                    <RadioGroupItem value={reason.code} />
                    <span>{reason.description}</span>
                  </label>
                ))}
              </RadioGroup>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900">รายละเอียดเพิ่มเติม (ถ้ามี)</p>
            <Textarea
              value={reasonDetail}
              onChange={(event) => onReasonDetailChange(event.target.value)}
              placeholder="อธิบายเพิ่มเติม"
            />
          </div>
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCancelling}>
            ปิด
          </Button>
          <Button
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={onConfirm}
            disabled={isCancelling || !selectedReason || cancelReasons.length === 0}
          >
            {isCancelling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            ยืนยันการยกเลิกเอกสาร
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
